import { useCallback, useEffect } from 'react';

import { Address } from 'viem';

import { OneInchNetwork, OneInchToken } from '@api';

import { ONE_INCH_NATIVE_NETWORK_TOKEN_ADDRESS } from '@shared/constants/magicSwap';
import displayWalletErrorToast from '@shared/helpers/displayWalletErrorToast';
import getUserTokenBalance, {
  initZeroTokenBalance,
  TokenBalance,
} from '@shared/helpers/web3/getUserTokenBalance';

import { MIN_VALUE } from '@magic-swap/components/pages/MagicSwap/constants';
import useSwitchNetworkUtil from '@magic-swap/components/pages/MagicSwap/hooks/useSwitchNetworkUtil';
import getVerifiedMagicSwapNetworkId from '@magic-swap/components/pages/MagicSwap/utils/getVerifiedMagicSwapNetworkId';

const useSwapTokenBalance = ({
  address,
  network,
  token,
  setTokenBalance,
}: {
  address?: Address;
  network?: OneInchNetwork;
  token?: OneInchToken;
  setTokenBalance: (value: TokenBalance) => void;
}) => {
  const { switchNetworkUtil } = useSwitchNetworkUtil();

  const getTokenBalance = useCallback(async () => {
    if (!network || !token || !address) {
      setTokenBalance(initZeroTokenBalance());
      return;
    }

    try {
      await switchNetworkUtil({ networkId: network.id });

      const tokenId = token.id === ONE_INCH_NATIVE_NETWORK_TOKEN_ADDRESS ? undefined : token.id;

      const verifiedNetworkId = getVerifiedMagicSwapNetworkId(network);

      const raw = await getUserTokenBalance({
        address,
        chainId: verifiedNetworkId,
        token: tokenId as Address,
      });

      const newBalance = raw.balance < MIN_VALUE ? initZeroTokenBalance() : raw;

      setTokenBalance(newBalance);
    } catch (error) {
      displayWalletErrorToast({ error });
      setTokenBalance(initZeroTokenBalance());
    }
  }, [address, network, token, setTokenBalance, switchNetworkUtil]);

  useEffect(() => {
    getTokenBalance().then(); // Just to calm IDE
  }, [getTokenBalance]);

  return {
    refetch: getTokenBalance,
  };
};

export default useSwapTokenBalance;
